import { cn } from "@/primitives/utils"

/*
 * The state of something, such as a connection or a Document's status, as a dot and a word:
 * "Connected", "Needs reconnecting", "Not connected". The dot's colour keeps its token's meaning,
 * so a fault to notice is `feed-error`, never a warning, something switched off is idle grey, and
 * something not yet begun, as a Document never released, is muted. `quiet` sets it in the small
 * muted type that follows a page's name in its header. Its label truncates where it runs out of
 * room, but with `wrap`, for a caller whose label must be read whole, as a card's state.
 */

const tones = {
  success: "bg-success ring-success/20",
  warning: "bg-warning ring-warning/20",
  "feed-error": "bg-feed-error ring-feed-error/20",
  idle: "bg-subtle-foreground ring-subtle-foreground/20",
  muted: "bg-muted-foreground ring-muted-foreground/20",
} as const

export type StatusDotTone = keyof typeof tones

export function StatusDot({
  tone,
  label,
  quiet = false,
  wrap = false,
}: {
  tone: StatusDotTone
  label: string
  quiet?: boolean
  wrap?: boolean
}) {
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center",
        quiet ? "gap-1.5 text-xs font-normal text-muted-foreground" : "gap-2 text-ui",
      )}
    >
      <span
        aria-hidden
        className={cn("shrink-0 rounded-full", quiet ? "size-1.5" : "size-2 ring-2", tones[tone])}
      />
      <span className={wrap ? "min-w-0" : "truncate"}>{label}</span>
    </span>
  )
}
